import { storageUrlForDisplay } from '@/shared/storageUrlForDisplay';

const TEXT_LINE_RE = /^(\s*)(-?\s*)text:\s*(.*)$/i;
const CACHE_URL_LINE_RE = /^(\s*)(-?\s*)cacheUrl:\s*(.*)$/i;
const TYPE_LINE_RE = /^(\s*)(-?\s*)type:\s*(.*)$/i;
const ALREADY_ENHANCED_TEXT_RE = /class=["'][^"']*citation-source-text/;

/** How many lines above ``cacheUrl:`` to scan for a preceding ``type:``. */
const TYPE_LOOKBACK_LINES = 8;

/** Yellow-bordered highlight for citation `text:` values (dark AnswerStyleMarkdown theme). */
export const CITATION_SOURCE_TEXT_MARK_CLASS =
  'citation-source-text inline-block border-2 border-yellow-400 rounded px-1.5 py-0.5 bg-yellow-400/20 text-white';

export type CitationCacheMediaKind = 'image' | 'video' | 'audio';

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'svg',
  'heic',
  'heif',
  'tif',
  'tiff',
]);
const VIDEO_EXTENSIONS = new Set([
  'mp4',
  'webm',
  'mov',
  'm4v',
  'avi',
  'mkv',
  'ogv',
  'mpeg',
  'mpg',
]);
const AUDIO_EXTENSIONS = new Set([
  'mp3',
  'wav',
  'm4a',
  'aac',
  'ogg',
  'flac',
  'opus',
  'wma',
]);

export function escapeHtmlForMarkdown(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Map a Fivecast ``type:`` value to image / video / audio when recognizable.
 */
export function citationCacheMediaKindFromType(
  raw: string | undefined | null,
): CitationCacheMediaKind | null {
  const t = (raw ?? '').trim().toLowerCase();
  if (!t) return null;
  if (
    /^(image|img|photo|picture|photoimage|still|jpeg|jpg|png|gif|webp|bmp|tiff?|heic|heif)$/.test(
      t,
    )
  ) {
    return 'image';
  }
  if (
    /^(video|movie|clip|mp4|webm|mov|m4v|avi|mkv|mpeg|mpg)$/.test(t)
  ) {
    return 'video';
  }
  if (
    /^(audio|sound|voice|voiceclip|mp3|wav|m4a|aac|ogg|flac|opus)$/.test(t)
  ) {
    return 'audio';
  }
  return null;
}

/**
 * Infer media kind from a cacheUrl path/extension (query string ignored).
 */
export function citationCacheMediaKindFromUrl(
  url: string | undefined | null,
): CitationCacheMediaKind | null {
  const path = (url ?? '').trim().split('?')[0].toLowerCase();
  if (!path) return null;
  const slash = path.lastIndexOf('/');
  const file = slash >= 0 ? path.slice(slash + 1) : path;
  const dot = file.lastIndexOf('.');
  if (dot < 0) return null;
  const ext = file.slice(dot + 1);
  if (IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (VIDEO_EXTENSIONS.has(ext)) return 'video';
  if (AUDIO_EXTENSIONS.has(ext)) return 'audio';
  return null;
}

/**
 * Prefer a preceding ``type:`` within a few lines above ``cacheUrl:``, else extension.
 */
export function resolveCitationCacheMediaKind(
  lines: string[],
  cacheUrlLineIndex: number,
  cacheUrl: string,
): CitationCacheMediaKind | null {
  const fromType = findPrecedingCitationType(lines, cacheUrlLineIndex);
  if (fromType) return fromType;
  return citationCacheMediaKindFromUrl(cacheUrl);
}

function findPrecedingCitationType(
  lines: string[],
  cacheUrlLineIndex: number,
): CitationCacheMediaKind | null {
  const start = Math.max(0, cacheUrlLineIndex - TYPE_LOOKBACK_LINES);
  for (let j = cacheUrlLineIndex - 1; j >= start; j--) {
    const line = lines[j];
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (isTopLevelSectionHeader(trimmed)) break;
    if (CACHE_URL_LINE_RE.test(line)) break;
    const typeMatch = line.match(TYPE_LINE_RE);
    if (typeMatch) {
      return citationCacheMediaKindFromType(typeMatch[3] ?? '');
    }
  }
  return null;
}

/**
 * Directory of the citation JSON (dispatched / preview / media URL) so relative
 * Fivecast ``cacheUrl: media/….jpeg`` paths resolve next to the JSON object.
 */
export function citationJsonMediaBaseUrl(paths: {
  dispatchedFilePath?: string | null;
  previewFilePath?: string | null;
  mediaUrl?: string | null;
  mediaSrc?: string | null;
  srcToUse?: string | null;
  originalUrl?: string | null;
}): string {
  const candidates = [
    paths.dispatchedFilePath,
    paths.previewFilePath,
    paths.mediaUrl,
    paths.mediaSrc,
    paths.srcToUse,
    paths.originalUrl,
  ];
  for (const candidate of candidates) {
    const path = (candidate ?? '').trim().split('?')[0];
    if (!path) continue;
    // Prefer paths that look like JSON extracts (archive_processing / .json).
    if (/\.json$/i.test(path) || /archive_processing\//i.test(path) || path.startsWith('s3://')) {
      const slash = path.lastIndexOf('/');
      if (slash >= 0) return path.slice(0, slash + 1);
    }
  }
  for (const candidate of candidates) {
    const path = (candidate ?? '').trim().split('?')[0];
    if (!path) continue;
    const slash = path.lastIndexOf('/');
    if (slash >= 0) return path.slice(0, slash + 1);
  }
  return '';
}

export function resolveCitationCacheUrl(
  cacheUrl: string,
  mediaBaseUrl: string | undefined | null,
): string {
  const raw = (cacheUrl ?? '').trim();
  if (!raw || /^none$/i.test(raw)) return '';
  if (/^s3:\/\//i.test(raw) || /^https?:\/\//i.test(raw) || raw.startsWith('/api/proxy/')) {
    return raw;
  }
  const base = (mediaBaseUrl ?? '').trim();
  if (!base) return raw;
  const baseDir = base.endsWith('/') ? base : `${base}/`;
  return `${baseDir}${raw.replace(/^\.\//, '')}`;
}

function isTopLevelSectionHeader(line: string): boolean {
  // Fivecast outline: `# Text #`, `# Images #` (single leading #).
  return /^#\s+[^#].*#\s*$/.test(line.trim());
}

function formatHighlightedTextLine(indent: string, bullet: string, textValue: string): string {
  const safe = escapeHtmlForMarkdown(textValue);
  return `${indent}${bullet}text: <mark class="${CITATION_SOURCE_TEXT_MARK_CLASS}">${safe}</mark>`;
}

function citationMediaMarkdownEmbed(
  kind: CitationCacheMediaKind,
  displaySrc: string,
): string {
  if (kind === 'video') return `![citation video](${displaySrc})`;
  if (kind === 'audio') return `![citation audio](${displaySrc})`;
  return `![citation image](${displaySrc})`;
}

/**
 * Heuristic: citation snippets that look like markdown (headers, lists, emphasis,
 * fences) rather than raw JSON / plain prose.
 */
export function looksLikeMarkdown(text: string | undefined | null): boolean {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed) return false;

  // Parseable JSON should not be treated as markdown (see resolveCitationMarkdownExcerpt).
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      JSON.parse(trimmed);
      return false;
    } catch {
      // fall through to markdown signals
    }
  }

  return (
    /^#{1,6}\s+\S/m.test(trimmed) ||
    /^(\s*[-*+]|\s*\d+\.)\s+\S/m.test(trimmed) ||
    /\*\*[^*]+\*\*|__[^_]+__/.test(trimmed) ||
    /```[\s\S]*```/.test(trimmed) ||
    /^\|.+\|/m.test(trimmed) ||
    /^(\s*[-*+]?\s*)?(text|cacheUrl|type):\s*/im.test(trimmed)
  );
}

const JSON_MARKDOWN_FIELD_KEYS = ['content', 'text', 'markdown', 'body', 'excerpt'] as const;

/**
 * Resolve the markdown excerpt for a JSON citation's ``content`` field.
 * When the string is itself parseable JSON (typical for JSON media citations),
 * look for a nested markdown-shaped property (TP-3588); otherwise test the raw string.
 */
export function resolveCitationMarkdownExcerpt(
  raw: string | undefined | null,
): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const record = parsed as Record<string, unknown>;
        for (const key of JSON_MARKDOWN_FIELD_KEYS) {
          const value = record[key];
          if (typeof value === 'string' && looksLikeMarkdown(value)) {
            return value.trim();
          }
        }
      }
      return null;
    } catch {
      // fall through to raw markdown check
    }
  }

  return looksLikeMarkdown(trimmed) ? trimmed : null;
}

/**
 * Enhance Fivecast-style citation ``content`` for markdown display:
 * - highlight each ``text:`` value with a yellow border
 * - embed each ``cacheUrl:`` media (image / video / audio) via the evidence proxy
 *   when ``type:`` (looked up a few lines above) or the file extension identifies it
 */
export function enhanceCitationMarkdownContent(
  content: string,
  options: { mediaBaseUrl?: string | null } = {},
): string {
  if (!content) return content;

  const lines = content.split('\n');
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const textMatch = line.match(TEXT_LINE_RE);
    if (textMatch && !ALREADY_ENHANCED_TEXT_RE.test(line)) {
      const [, indent, bullet, rawValue] = textMatch;
      out.push(formatHighlightedTextLine(indent, bullet, rawValue ?? ''));
      continue;
    }

    const cacheMatch = line.match(CACHE_URL_LINE_RE);
    if (cacheMatch) {
      out.push(line);
      const cacheUrl = (cacheMatch[3] ?? '').trim();
      const kind = resolveCitationCacheMediaKind(lines, i, cacheUrl);
      if (kind) {
        const resolved = resolveCitationCacheUrl(cacheUrl, options.mediaBaseUrl);
        const displaySrc = resolved ? storageUrlForDisplay(resolved) : '';
        if (displaySrc) {
          out.push('');
          out.push(citationMediaMarkdownEmbed(kind, displaySrc));
          out.push('');
        }
      }
      continue;
    }

    out.push(line);
  }

  return out.join('\n');
}
